/**
 * SpaceSwitcher — sidebar dropdown that lets the user switch the
 * active workspace. Changing the active space reloads the SPA so
 * every cached query / derived state is rebuilt from scratch under
 * the new scope (per product decision: full reload, not soft).
 *
 * Two render modes:
 *   • full (default)   — wide button with space name + role chip.
 *   • rail             — icon-only square button (collapsed sidebar).
 */
import { Briefcase, Check, ChevronDown, Crown, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { cn } from "../lib/utils";
import { tx } from "../i18n";
import { useLang } from "../contexts/LangContext";
import { useSpace } from "../contexts/SpaceContext";
import { WorkspacePlanChip } from "./WorkspacePlanChip";

const ROLE_TONE = {
  owner:  "bg-violet-100 text-violet-700",
  editor: "bg-emerald-100 text-emerald-700",
  viewer: "bg-slate-100 text-slate-600",
};

function RoleChip({ role }) {
  const tone = ROLE_TONE[role] || ROLE_TONE.viewer;
  return (
    <span className={cn("text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full", tone)}>
      {role}
    </span>
  );
}

export function SpaceSwitcher({ rail = false }) {
  const { lang } = useLang();
  const { activeSpace, spaces, switchSpace, isLoading, isMultiWorkspaceUser } = useSpace();

  // iter-170am-part5 — Hide the dropdown entirely when the user has
  // only their personal space AND no entitlement to add more. The
  // sidebar stays clean for single-workspace users — but we STILL
  // surface the plan chip so the user knows whether they're on Free
  // or a paid tier (and can click to /pricing).
  if (!isMultiWorkspaceUser) {
    return (
      <div
        className={cn(
          "flex",
          rail ? "justify-center" : "justify-end px-2.5 py-1.5",
        )}
        data-testid="space-switcher-hidden-plan-only"
      >
        <WorkspacePlanChip rail={rail} />
      </div>
    );
  }

  if (isLoading || spaces.length === 0) {
    return (
      <div
        className={cn(
          "rounded-xl bg-slate-100 animate-pulse",
          rail ? "h-9 w-9 mx-auto" : "h-9 w-full",
        )}
        data-testid="space-switcher-skeleton"
      />
    );
  }

  const onPick = (id) => {
    if (id !== activeSpace?.id) switchSpace(id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid="space-switcher-trigger"
        className={cn(
          "group flex items-center gap-2 rounded-xl border border-slate-200 bg-white",
          "hover:bg-slate-50 transition-colors text-left",
          rail ? "h-9 w-9 justify-center p-0" : "w-full px-2.5 py-1.5",
        )}
        title={activeSpace?.name}
      >
        <Briefcase className="h-4 w-4 shrink-0 text-slate-500" />
        {!rail && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 leading-none">
                {tx(lang, "Space", "Espacio", "Espaço")}
              </div>
              <div className="mt-0.5 text-xs font-medium text-slate-700 truncate">
                {activeSpace?.name || "—"}
              </div>
            </div>
            {activeSpace?.my_role && <RoleChip role={activeSpace.my_role} />}
            {/* iter-170an follow-up — Plan chip next to the role chip
                so the user always sees their workspace's plan tier
                and can jump to /pricing in one click. */}
            <WorkspacePlanChip />
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          </>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64"
        data-testid="space-switcher-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
          {tx(lang, "Switch space", "Cambiar de espacio", "Trocar de espaço")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {spaces.map((s) => {
          const isActive = s.id === activeSpace?.id;
          const Icon = s.kind === "team" ? Users : Crown;
          return (
            <DropdownMenuItem
              key={s.id}
              onClick={() => onPick(s.id)}
              data-testid={`space-switcher-item-${s.id}`}
              className="gap-2 cursor-pointer"
            >
              <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              <span className="flex-1 min-w-0 truncate text-xs">{s.name}</span>
              {s.my_role && <RoleChip role={s.my_role} />}
              {isActive && <Check className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
